import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Calendar as CalendarIcon, BarChart3, ListChecks, LogOut, Menu, X, Languages, Home, Star, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO = "/logo.png";

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
      isActive ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-card hover:text-foreground"
    }`;

  const NavItems = () => (
    <>
      <NavLink to="/portal/calendar" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-calendar">
        <CalendarIcon className="h-4 w-4" /> {t.portal.nav.calendar}
      </NavLink>
      <NavLink to="/portal/jobs" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-jobs">
        <ListChecks className="h-4 w-4" /> {t.portal.nav.jobs}
      </NavLink>
      <NavLink to="/portal/reports" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-reports">
        <BarChart3 className="h-4 w-4" /> {t.portal.nav.reports}
      </NavLink>
      <NavLink to="/portal/reviews" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-reviews">
        <Star className="h-4 w-4" /> {t.portal.nav.reviews}
      </NavLink>
      <NavLink to="/portal/gallery" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-gallery">
        <ImageIcon className="h-4 w-4" /> {t.portal.nav.gallery}
      </NavLink>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="portal-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 border-r border-border flex-col p-4 sticky top-0 h-screen">
        <Link to="/" className="flex items-center justify-center pb-6 border-b border-border">
          <img src={LOGO} alt="logo" className="h-12 w-auto" />
        </Link>
        <nav className="flex-1 mt-6 space-y-1">
          <NavItems />
        </nav>
        <div className="pt-4 border-t border-border space-y-2">
          <div className="px-3 py-2 text-xs text-muted-foreground">
            {t.portal.welcome}, <span className="text-foreground font-semibold">{user?.name}</span>
          </div>
          <button onClick={toggle} className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-card transition-colors" data-testid="portal-lang-toggle">
            <Languages className="h-4 w-4" /> {lang === "en" ? "Español" : "English"}
          </button>
          <Link to="/" className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-card transition-colors">
            <Home className="h-4 w-4" /> Website
          </Link>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors" data-testid="portal-logout-btn">
            <LogOut className="h-4 w-4" /> {t.portal.nav.logout}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div
        className="lg:hidden fixed top-0 inset-x-0 z-40 backdrop-blur-xl border-b border-border/60 bg-background/95"
      >
        <div className="flex items-center justify-between px-4 py-2">
          <Link to="/portal/calendar" className="flex items-center gap-2">
            <img src={LOGO} alt="logo" className="h-9 w-auto" />
          </Link>
          <button onClick={() => setOpen(!open)} className="p-2 rounded-md border border-border/60" data-testid="portal-mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <div className="border-t border-border/60 p-4 space-y-1 bg-background/95">
            <NavItems />
            <button onClick={toggle} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-card transition-colors">
              <Languages className="h-4 w-4" /> {lang === "en" ? "Español" : "English"}
            </button>
            <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-muted-foreground">
              <Home className="h-4 w-4" /> Website
            </Link>
            <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-destructive">
              <LogOut className="h-4 w-4" /> {t.portal.nav.logout}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <main className="flex-1 min-w-0 pt-16 lg:pt-0">
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
